import { ReactNode } from "react";
import { useLocation } from "react-router-dom-v5-compat";

import { ContactAlreadyExistsError } from "@seneca/tutoring-service-client";
import { ContactRequestProperties } from "@seneca/tutoring-service-types";

import { regionCodes, regions } from "common/static/regions";

import { getRegion } from "seneca-common/features/user/state";
import { FormProvider } from "seneca-common/utils/form";
import { OnSubmitSuccess } from "seneca-common/utils/form/types";
import { normaliseFormsData } from "seneca-common/utils/form/utils";
import { useSimpleRedirectCallback } from "seneca-common/utils/hooks/routing";
import { useSelector } from "seneca-common/utils/hooks/selectors";

import { Form } from "features/parents/claim-tutoring-journey";
import { useIsPlurallApp } from "features/plurall";
import { MODAL_ROUTES } from "routing/routes";

import { TutoringFormModalStep } from "../features/tutoring-form-modal/consts";
import useGoToStep from "../features/tutoring-form-modal/hooks/useGoToStep";
import { analyticsLogUserSubmittedForm } from "./analytics";
import { useGetSubjects } from "./components/EmeddedTutoringForm/hooks";
import { useCreateContact } from "./hooks/useCreateContact";
import useFetchLead from "./hooks/useFetchLead";
import useGetTutoringLeadSource from "./hooks/useGetTutoringLeadSource";
import { useUpdateContact } from "./hooks/useUpdateContact";
import { formatCountryCodeAndPhoneNumber } from "./utils/formatCountryCodeAndPhoneNumber";

type Props = {
  children: ReactNode;
  form?: Form;
};

export default function ClaimTutoringJourney({
  children,
  form = Form.LeadDetails
}: Props) {
  const isPlurallApp = useIsPlurallApp();
  const isGB = useSelector(getRegion) === regionCodes.GB;
  const location = useLocation();
  const onFormSubmitWithServiceCall = useOnFormSubmitWithServiceCall(form);
  const { redirectOnFormSubmit } = useOnFormSubmitRedirect(form);
  const closeModal = useSimpleRedirectCallback(location.pathname);

  if (isPlurallApp || !isGB) closeModal();

  async function onSubmitSuccess(onSubmitSuccessProps: OnSubmitSuccess) {
    const formIsFilledOut = onSubmitSuccessProps.validateForm(form);

    if (formIsFilledOut) {
      await onFormSubmitWithServiceCall(onSubmitSuccessProps);
      return;
    }

    redirectOnFormSubmit(onSubmitSuccessProps);
  }

  return (
    <FormProvider onSubmitSuccess={onSubmitSuccess}>{children}</FormProvider>
  );
}

function useOnFormSubmitRedirect(form: Form) {
  const location = useLocation();
  const source = useGetTutoringLeadSource();

  const onModalCloseClick = useSimpleRedirectCallback(location.pathname);

  const linkToSuccess = {
    pathname: MODAL_ROUTES.CLAIM_TUTORING_SUCCESS.buildPath({}),
    search: MODAL_ROUTES.CLAIM_TUTORING_SUCCESS.buildSearch({
      oneToOneTutoringSource: source
    }),
    state: MODAL_ROUTES.CLAIM_TUTORING_SUCCESS.buildState({
      background: location
    })
  };

  const tutoringPageRedirectToSuccess =
    useSimpleRedirectCallback(linkToSuccess);

  const goToTutoringFormModalStep = useGoToStep();
  const modalJourneyRedirectToSuccess = () =>
    goToTutoringFormModalStep(TutoringFormModalStep.tutoringType);

  const redirectToSuccess =
    form === Form.LeadDetailsModalJourney
      ? modalJourneyRedirectToSuccess
      : tutoringPageRedirectToSuccess;

  function redirectOnFormSubmit({ form }: OnSubmitSuccess) {
    switch (form) {
      case Form.LeadDetails:
        tutoringPageRedirectToSuccess();
        break;
      case Form.LeadDetailsModalJourney:
        modalJourneyRedirectToSuccess();
        break;
      default:
        onModalCloseClick();
        break;
    }
  }

  return { redirectOnFormSubmit, redirectToSuccess };
}

function useOnFormSubmitWithServiceCall(form: Form) {
  const country = useSelector(getRegion) ?? "GB";
  const subjects = useGetSubjects();

  const { redirectToSuccess } = useOnFormSubmitRedirect(form);
  const { data: userTutoringLead } = useFetchLead();

  const { mutateAsync: createContact } = useCreateContact({
    onSuccess: redirectToSuccess
  });
  const { mutateAsync: updateContact } = useUpdateContact({
    onSuccess: redirectToSuccess
  });

  const { name: countryName } = regions[country];
  const source = useGetTutoringLeadSource();

  return async ({ data, setFormLoading, setFormErrors }: OnSubmitSuccess) => {
    setFormLoading(true);

    const formsData = normaliseFormsData({
      formsData: data,
      enrichment: {
        country: countryName,
        tutoringSource: source
      },
      deletions: ["agreeTermsAndConditions"]
    });

    const subjectObj = subjects.find(
      (subject: { id: string }) => subject.id === formsData.subject
    );

    const { countryCode, subject, ...formsDataWithoutCountryCodeAndSubject } =
      formsData;

    const formattedPhoneNumber = formatCountryCodeAndPhoneNumber(
      countryCode,
      formsData.phone
    );

    const submitFormData: ContactRequestProperties = {
      ...formsDataWithoutCountryCodeAndSubject,
      phone: formattedPhoneNumber,
      subject: subjectObj?.name ?? subject
    };

    try {
      if (userTutoringLead) {
        await updateContact({
          ...submitFormData,
          trialLastRerequestDate: new Date().toISOString()
        });
      } else {
        await createContact(submitFormData);
      }

      analyticsLogUserSubmittedForm({
        yearGroup: submitFormData.yearGroup,
        subject: submitFormData.subject,
        source,
        form
      });
    } catch (e: any) {
      if (e instanceof ContactAlreadyExistsError) {
        setFormErrors([
          "This e-mail address has already requested 1-to-1. We'll be in touch soon."
        ]);
      } else {
        setFormErrors([
          `There is an error with your submission. Please retry.`
        ]);
      }
    }

    setFormLoading(false);
  };
}
